import { ApiError, readProblem } from './problem'

/**
 * عميلُ الـAPI — <b>ورمزُ الوصول في الذاكرة وحدَها</b>.
 *
 * هذا ليس اختيارَ راحة: الخادمُ صمّم التسليمَ هكذا عمدًا (انظر
 * `AuthEndpoints`). رمزُ التحديث في كوكي `HttpOnly` لا تقرؤها JavaScript، ورمزُ
 * الوصول في متغيّرٍ يموت بإغلاق التبويب. ولو حُفظ في `localStorage` لَصار كلُّ
 * XSS سرقةَ حسابٍ كاملة، ولو حُفظ في كوكي لَصارت كلُّ نقطةٍ تغيّر حالةً سطحًا
 * لـCSRF بدل نقطتين.
 *
 * وثمنُه أنّ تحديثَ الصفحة يُفقد رمزَ الوصول — ويعوّضه التدويرُ الصامت أدناه.
 */

/** رمزُ الوصول. لا يُصدَّر: من أراده ناداه عبر الدوالّ. */
let accessToken: string | null = null

/** يُنادى حين تسقط الجلسةُ نهائيًّا، فتُخرج الواجهةُ الموظّفَ إلى الدخول. */
let onSessionLost: (() => void) | null = null

/**
 * تدويرٌ واحدٌ في الطيران — <b>وهذا الحارسُ ليس تحسينَ أداء</b>.
 *
 * شاشةُ المراجعة تُطلق أربعةَ طلباتٍ معًا. لو انتهى رمزُ الوصول لَردّت أربعتُها
 * 401 معًا، ولَأطلق كلٌّ منها تدويرًا. والخادمُ يُدوّر رمزَ التحديث في كلّ
 * نداء، فالثاني يصل برمزٍ استُهلك ⇒ `TOKEN_REUSE_DETECTED` ⇒ تُبطَل
 * <b>السلسلةُ كلُّها</b>، فيُطرد الموظّفُ من كلّ أجهزته لأنّ شاشةً واحدةً
 * حمّلت أربعةَ أشياءَ في وقتٍ واحد.
 */
let refreshing: Promise<boolean> | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function hasAccessToken() {
  return accessToken !== null
}

export function onSessionLostDo(handler: (() => void) | null) {
  onSessionLost = handler
}

// ── الاتّصال (`0052`) ─────────────────────────────────────────────────────
//
// ⚠ **«فاصل» يُعرف من النداء نفسِه لا من المتصفّح وحدَه**: `navigator.onLine`
//   يقول «متّصل» والراوترُ بلا إنترنت. فكلُّ نداءٍ لم يصل خادمَه يقول «فاصل»،
//   وكلُّ جوابٍ وصل يقول «متّصل» — و٥٠٢/٥٠٣/٥٠٤ من البوّابة تعني أنّ الخادمَ لا
//   يجيب، فهي «فاصل» أيضًا: الصندوقُ لا يبيع على خادمٍ لا يردّ.

let online = true
const connectivity = new Set<(online: boolean) => void>()

export function isOnline() {
  return online
}

export function onConnectivity(listener: (online: boolean) => void): () => void {
  connectivity.add(listener)

  return () => {
    connectivity.delete(listener)
  }
}

function report(next: boolean) {
  if (online !== next) {
    online = next
    connectivity.forEach((listener) => listener(next))
  }
}

/** هل الخطأُ انقطاعٌ لا جوابٌ رافض؟ — <b>والجوابُ الرافضُ <c>ApiError</c> دائمًا</b>. */
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof ApiError && [502, 503, 504].includes(error.status))
}

/** يسأل الخادمَ «أأنت هنا؟» — ويُبلغ النتيجة. */
export async function probeServer(): Promise<boolean> {
  try {
    const res = await fetch('/health/ready', { cache: 'no-store' })
    report(res.ok)

    return res.ok
  } catch {
    report(false)

    return false
  }
}

/**
 * قيمةُ كوكي CSRF — تُقرأ بـJavaScript عمدًا وتُعاد في ترويسة.
 *
 * والاسمُ يسقط بادئةَ `__Host-` في التطوير على http، تمامًا كما يفعل الخادم:
 * المتصفّحُ يرفض البادئةَ بلا `Secure`. فيُبحث عن الاسمين معًا، ولا يُفترض
 * أيُّهما — واجهةٌ تبحث عن اسمٍ واحد تعمل في بيئةٍ وتسقط في الأخرى بلا رسالة.
 */
function csrfToken(): string | null {
  for (const name of ['__Host-shop_csrf', 'shop_csrf']) {
    const prefix = `${name}=`
    const hit = document.cookie.split('; ').find((c) => c.startsWith(prefix))

    if (hit) {
      return decodeURIComponent(hit.slice(prefix.length))
    }
  }

  return null
}

type RequestOptions = {
  method?: string
  body?: unknown

  /**
   * نموذجٌ متعدّدُ الأجزاء — للملفّات.
   *
   * ⚠ ولا تُضبط له `Content-Type` بيد: المتصفّحُ يكتبها ومعها الفاصلُ
   *   (`boundary`) الذي يولّده. وضبطُها يدويًّا يُنتج ترويسةً بلا فاصل، فيردّ
   *   الخادمُ «ليس نموذجًا» على نموذجٍ سليم.
   */
  form?: FormData

  /** طلبٌ لا يُدوَّر له رمز: الدخولُ والتدويرُ نفسُه — وإلا صارت الحلقةُ لانهائيّة. */
  anonymous?: boolean
  signal?: AbortSignal
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {}

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  if (accessToken && !options.anonymous) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  const csrf = csrfToken()

  if (csrf) {
    headers['X-Csrf-Token'] = csrf
  }

  let res: Response

  try {
    res = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),

      // الكوكي أصلٌ واحدٌ مع الـAPI (لا CORS)، و`same-origin` هو الافتراض —
      // يُكتب صراحةً لأنّ اعتمادَ الجلسة عليه.
      credentials: 'same-origin',
      signal: options.signal,
    })
  } catch (failure) {
    // والإلغاءُ ليس انقطاعًا: شاشةٌ أُغلقت فأُلغي نداؤها.
    if (!(failure instanceof DOMException && failure.name === 'AbortError')) {
      report(false)
    }

    throw failure
  }

  report(![502, 503, 504].includes(res.status))

  return res
}

/**
 * اسمُ القفل بين التبويبات — <b>واحدٌ للأصل كلِّه</b>.
 *
 * وأقفالُ الويب مقصورةٌ على الأصل، ونقطةُ البيع والإدارةُ أصلان منفصلان،
 * فلا يتزاحمان على قفلٍ واحد.
 */
const REFRESH_LOCK = 'shop:auth:refresh'

/**
 * التدويرُ الفعليّ — <b>ويكتب الكوكيَّ الجديد بردّ الخادم</b>.
 */
async function rotate(): Promise<boolean> {
  try {
    const res = await send('/api/v1/auth/refresh', { method: 'POST', body: {}, anonymous: true })

    if (!res.ok) {
      return false
    }

    const body = (await res.json()) as { accessToken: string }
    accessToken = body.accessToken

    return true
  } catch {
    return false
  }
}

/**
 * يُدوّر مرّةً واحدةً مهما تعدّد الطالبون — <b>وفي التبويبات كلِّها لا في هذا
 * وحدَه</b>.
 *
 * ⚠ <b>والقفلُ داخلَ التبويب لم يكن يكفي، ووقع فعلًا:</b> تبويبان مفتوحان
 *   يحملان الكوكيَّ نفسَه، فيُقلعان معًا ويُرسلان تدويرَين بالرمز نفسِه.
 *   فيُدوّره الخادمُ مرّتين، ويصير للرمز الواحد خلَفان — <b>وأوّلُ نداءٍ بعدها
 *   يراه «رمزًا دُوِّر وعاد يُستعمل» فيُبطل السلسلةَ كلَّها</b>.
 *
 *   وقُرئ في سجلّ الجلسات: ثلاثُ جلساتٍ لموظّفٍ واحدٍ في <b>الميلّي ثانية
 *   نفسِها</b>، ثمّ أُبطلت ثلاثتُها معًا بعد ثانيتَي عشرة. فطُرد من كلّ أجهزته
 *   لأنّ شاشةً فُتحت مرّتين.
 *
 * ⚠ <b>وقفلُ الويب يزيل السببَ ولا يُضعف حارسًا</b>: الثاني ينتظر الأوّلَ،
 *   فيجد الكوكيَّ الجديدَ قد كُتب — والكوكيُّ مشترَكٌ بين تبويبات المتصفّح —
 *   فيُدوّر برمزٍ صحيحٍ ولا يبدو لصًّا.
 *
 *   و<c>navigator.locks</c> قد لا يوجد (سياقٌ غيرُ آمن، أو متصفّحٌ قديم)،
 *   فيُرجَع إلى القفل داخل التبويب وحدَه — <b>وهو أفضلُ من لا شيء</b>.
 */
function refreshOnce(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const locks = globalThis.navigator?.locks

      return locks
        ? await locks.request(REFRESH_LOCK, rotate)
        : await rotate()
    } catch {
      return false
    } finally {
      // يُفرَغ بعد انتهاء الوعد لا قبله: لو فُرغ قبله لَبدأ الطالبُ التالي
      // تدويرًا ثانيًا على الرمز نفسِه — وهو بعينه ما يحرسه هذا القفل.
      queueMicrotask(() => {
        refreshing = null
      })
    }
  })()

  return refreshing
}

/**
 * نداءٌ واحد: يُرسل، وإن ردّ 401 دوّر مرّةً وأعاد الإرسالَ مرّةً واحدة.
 *
 * ومرّةً واحدة عمدًا: 401 بعد تدويرٍ ناجحٍ يعني أنّ المشكلةَ ليست في عمر الرمز
 * (حسابٌ عُطّل، أو رُفعت نسخةُ صلاحيّاته). وإعادةُ المحاولة حينها حلقةٌ لا تنتهي.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await send(path, options)

  if (res.status === 401 && !options.anonymous) {
    if (await refreshOnce()) {
      res = await send(path, options)
    }

    if (res.status === 401) {
      accessToken = null
      onSessionLost?.()
      throw await readProblem(res)
    }
  }

  if (!res.ok) {
    throw await readProblem(res)
  }

  if (res.status === 204) {
    return undefined as T
  }

  return (await res.json()) as T
}

/**
 * يرفع ملفًّا واحدًا — ويردّ لا شيء (الخادمُ يردّ 204).
 *
 * ونداءٌ مستقلٌّ عن <c>api</c> لأنّ الجسدَ ليس JSON: لو مرّ من هناك لَصار
 * `JSON.stringify(FormData)` وهو `{}`.
 */
export async function apiUpload(path: string, file: File, field = 'file'): Promise<void> {
  const form = new FormData()
  form.append(field, file, file.name)

  await api<void>(path, { method: 'POST', form })
}

/**
 * يجلب بايتاتٍ (صورةً) بالمصادقة نفسِها — ويردّ <c>null</c> إن لم توجد.
 *
 * <b>ولماذا لا `&lt;img src&gt;` وحسب:</b> رمزُ الوصول في الذاكرة ويُرسل في
 * ترويسة `Authorization`، والمتصفّحُ لا يُرسل الترويسةَ مع الصور. فالبديلُ
 * إمّا مسارٌ عامٌّ بلا جلسة — وصورُ الإدخال فيها أرقامُ IMEI تُقرأ — وإمّا هذا.
 *
 * و404 ليست خطأً هنا: صنفٌ بلا صورةٍ حالةٌ عاديّة، لا شيءَ يُعرض وحسب.
 */
export async function apiImage(path: string, signal?: AbortSignal): Promise<Blob | null> {
  let res = await send(path, { signal })

  if (res.status === 401) {
    if (await refreshOnce()) {
      res = await send(path, { signal })
    }

    if (res.status === 401) {
      accessToken = null
      onSessionLost?.()

      return null
    }
  }

  if (res.status === 404) {
    return null
  }

  if (!res.ok) {
    throw await readProblem(res)
  }

  return await res.blob()
}

export { ApiError }
